(ns frontend.view.modals.fund-edit.view
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.transaction-process.config :as config]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.utils :as fu]
   [frontend.view.components.clipboard :as clipboard]
   [frontend.view.modals.fund-edit.events :as events]
   [reagent.core :as r]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.view.transaction-process.fund-config :as fund-config]))

(defonce modal-key :fund-edit-modal)

(defn not-unique-fund?
  [existing-funds fund-name]
  (let [lc (fu/trimmed-lower-case fund-name)]
    (contains? (->> existing-funds
                    (map :fund/name)
                    (map fu/trimmed-lower-case)
                    (set))
               lc)))

(defn ref-multi-options
  [field-schema]
  (->> (fu/option-entities nil field-schema)
       (mapv (fn [e]
               {:value (:db/id e)
                :label (entity/display-string e)}))))

(defn ref-multi-value
  [v]
  (->> v
       (mapv (fn [item]
               (if (map? item)
                 (:db/id item)
                 item)))))

(defn ref-multi-input
  [{:keys [state attribute label field-schema]}]
  (let [value (ref-multi-value (get @state attribute))
        options (ref-multi-options field-schema)]
    [labelled-input/with-actions
     {:tools (clipboard/copy-tool value options)}
     [labelled-input/view
      {:editor-type :editor.type/multi-select
       :data-type   :data.type/ref-multi
       :label       label
       :value       value
       :options     options
       :on-change   (fn [new-value & _]
                      (swap! state assoc attribute (vec new-value)))}]]))

(defn view-body
  [state existing-funds]
  (let [tenant-schema @session/tenant-schema
        fund-teams-schema (registry/get-field-schema tenant-schema :fund/teams)
        fund-reporting-admins-schema (registry/get-field-schema tenant-schema
                                                                :fund/reporting-admins)
        fund-feeder-fund-to-schema (registry/get-field-schema tenant-schema :fund/feeder-fund-to)
        lux-fund-schema (registry/get-field-schema tenant-schema :fund/lux-fund?)]
    [:div
     {:class "flex flex-col gap-3"}
     [labelled-input/view
      {:data-type  :data.type/text-simple
       :label      (loc/tr :name)
       :mandatory? true
       :value      (get @state :fund/name existing-funds)
       :on-change  (fn [new-value]
                     (swap! state assoc :fund/name new-value)
                     (let [err (when (not-unique-fund? existing-funds new-value)
                                 (loc/tr :this-fund-already-exists))]
                       err))}]
     [ref-multi-input
      {:state        state
       :attribute    :fund/teams
       :label        (loc/field-name :fund/teams fund-teams-schema)
       :field-schema fund-teams-schema}]
     (when (and (session/has-feature-access? :feature/lp-reporting)
                (session/is-admin?))
       [ref-multi-input
        {:state        state
         :attribute    :fund/reporting-admins
         :label        (loc/field-name :fund/reporting-admins fund-reporting-admins-schema)
         :field-schema fund-reporting-admins-schema}])
     (when (and (session/is-admin?)
                (f-permissions/field-write-access? :fund/feeder-fund-to :entity.type/fund nil))
       [ref-multi-input
        {:state        state
         :attribute    :fund/feeder-fund-to
         :label        (loc/field-name :fund/feeder-fund-to fund-feeder-fund-to-schema)
         :field-schema fund-feeder-fund-to-schema}])
     (when (and (session/is-admin?)
                (f-permissions/field-write-access? :fund/lux-fund? :entity.type/fund nil))
       [labelled-input/view
        {:data-type :data.type/boolean
         :label     (loc/field-name :fund/lux-fund? lux-fund-schema)
         :value     (boolean (:fund/lux-fund? @state))
         :on-change (fn [new-value]
                      (swap! state assoc :fund/lux-fund? new-value))}])
     (when (and (session/is-admin?)
                (session/has-feature-access? :feature/transaction-process))
       [fund-config/view
        {:value     (config/parse-vehicle-config (:fund/transaction-process-config @state))
         :on-change (fn [new-config]
                      (swap! state assoc :fund/transaction-process-config new-config))}])]))

(defn footer-view
  [{:keys [state duplicate-name? no-changes? on-close!]}]
  [modal/right-single-button
   nil
   (or duplicate-name?
       no-changes?
       (not (events/form-ready? state)))
   (fn [_]
     (events/save! state on-close!))
   (loc/tr :save)
   nil])

(defn no-changes?
  [new-state old-state]
  (let [basic-unchanged? (->> [:fund/name :fund/teams :fund/reporting-admins :fund/feeder-fund-to]
                              (remove (fn [attr]
                                        (= (ref-multi-value (get new-state attr))
                                           (ref-multi-value (get old-state attr)))))
                              (empty?))
        lux-fund-unchanged? (= (:fund/lux-fund? new-state)
                               (:fund/lux-fund? old-state))
        config-unchanged? (= (config/parse-vehicle-config (:fund/transaction-process-config new-state))
                             (config/parse-vehicle-config (:fund/transaction-process-config old-state)))]
    (and basic-unchanged?
         lux-fund-unchanged?
         config-unchanged?)))

(defn view
  [{:keys [_shown? _close-modal! props _class-name _topmost?]}]
  (let [state (r/atom (:selected-fund props))]
    (fn [{:keys [shown? close-modal! props _class-name topmost?]}]
      (let [{:keys [existing-funds selected-fund _fund-id on-close!]} props
            not-unique-fund? (not-unique-fund? existing-funds (:fund/name @state))
            no-changes? (no-changes? @state selected-fund)
            on-close! (fn []
                        (when on-close!
                          (on-close!))
                        (close-modal!))]
        [modal/view
         {:visible?     (and shown? topmost?)
          :hide!        close-modal!
          :backdrop     "static"
          :success-text (loc/tr :success-fund-updated-or-created)
          :on-close!    on-close!}
         (loc/tr :fund-edit-modal/title)
         [view-body state existing-funds]
         [footer-view
          {:state state
           :not-unique-fund? not-unique-fund?
           :no-changes? no-changes?
           :on-close! on-close!}]]))))
